/**
 * web/src/components/layout/AppShell.tsx
 * Application shell — Sidebar (left) + ReconnectingBanner (top-of-content)
 * + Topbar + page content (Outlet). The grid layout is a CSS grid with:
 *   - lg: 248px sidebar | 1fr content
 *   - md: 56px icon-rail sidebar | 1fr content
 *   - sm: single column (sidebar hidden; <MobileSidebar /> drawer shows
 *     via the hamburger button overlaid on the topbar)
 *
 * Mounts the <TopbarProvider> so any page rendered through <Outlet /> can
 * call `useSetTopbar` to publish its title / breadcrumbs / actions.
 *
 * The `pending` prop renders a full-page skeleton in place of <Outlet />
 * while /api/me is still resolving (App.tsx's RequireAuth passes `pending`
 * during the initial fetch). The visual frame is unchanged so the user
 * doesn't see the dashboard layout shift when auth lands.
 *
 * Design source-of-truth: planning-docs/design_handoff_pixel_dashboard/
 * design_files/styles.css `.app` grid (lines 108-114) + `.main` content
 * column (lines 376-382).
 */
import { useState } from 'react';
import { Outlet } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { Topbar, TopbarProvider } from './Topbar';
import { ReconnectingBanner } from './ReconnectingBanner';
import { MobileSidebar } from './MobileSidebar';
import { Skeleton } from '../shared/Skeleton';
import { Icon } from '../../lib/icons';
import { cn } from '../../lib/cn';
import { EventProvider } from '../../hooks/useEvent';
import { BatchRunProvider } from '../speaker/BatchRunContext';
import { SpeakerBatchDrawer } from '../speaker/SpeakerBatchDrawer';
import { BatchRunDock } from '../speaker/BatchRunDock';

/** Public props for AppShell. */
export interface AppShellProps {
  /**
   * When true, render a full-page skeleton in place of <Outlet />. Used
   * by App.tsx's RequireAuth during the initial /api/me fetch so the
   * shell renders immediately without flashing a redirect.
   */
  pending?: boolean;
}

/**
 * Top-level dashboard layout. Renders Sidebar + ReconnectingBanner +
 * Topbar + Outlet inside a TopbarProvider. The grid columns adapt at
 * `md` and `lg` breakpoints (CONTEXT.md D-3.1, D-3.2). At sm a hamburger
 * button overlays the topbar and toggles the <MobileSidebar /> drawer.
 *
 * @sideEffects mounts <TopbarProvider> state; subscribes to WS via
 *              <Sidebar> + <ReconnectingBanner> children; holds
 *              `drawerOpen` state for the mobile drawer.
 * @idempotent yes — Outlet content owns its own data subscriptions
 */
export function AppShell({ pending = false }: AppShellProps) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  return (
    <TopbarProvider>
      <EventProvider>
        <BatchRunProvider>
          <div
            className={cn(
              'grid h-screen w-full overflow-hidden',
              'grid-cols-1 md:grid-cols-[56px_1fr] lg:grid-cols-[248px_1fr]',
            )}
          >
            <Sidebar />
            <div className="flex flex-col min-w-0 min-h-0 overflow-hidden">
              <ReconnectingBanner />
              {/* Hamburger overlay sits on top of the topbar at sm only.
                Topbar receives sm:pl-14 so the title doesn't collide. */}
              <div className="relative">
                <button
                  type="button"
                  aria-label="Open menu"
                  onClick={() => setDrawerOpen(true)}
                  className="hidden sm:flex md:hidden absolute left-3 top-1/2 -translate-y-1/2 z-20 w-8 h-8 rounded-md items-center justify-center bg-bg border border-border text-text hover:bg-bg-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent focus-visible:outline-offset-2"
                >
                  <Icon name="more" size={16} />
                </button>
                <Topbar className="sm:pl-14" />
              </div>
              <main className={cn('flex-1 overflow-auto', 'bg-bg px-9 py-8', 'sm:px-4 sm:py-4')}>
                {pending ? <PendingShell /> : <Outlet />}
              </main>
            </div>
          </div>
          <MobileSidebar open={drawerOpen} onOpenChange={setDrawerOpen} />
          {/* Batch actions: the drawer + persistent run dock live ABOVE the
              <Outlet /> so the dock survives navigation between speaker pages
              and reopening the drawer works on any route. */}
          <SpeakerBatchDrawer />
          <BatchRunDock />
        </BatchRunProvider>
      </EventProvider>
    </TopbarProvider>
  );
}

/**
 * Full-page skeleton rendered when AppShell is in `pending` mode. Mirrors
 * the wide-content layout of the dashboard pages (a header row + 3
 * stacked cards) so the user sees the shape of the upcoming view land
 * before the data fills in.
 */
function PendingShell() {
  return (
    <div className="max-w-[1200px] flex flex-col gap-4">
      <Skeleton variant="row" height={48} />
      <Skeleton variant="card" />
      <Skeleton variant="card" />
      <Skeleton variant="card" />
    </div>
  );
}
